import { extractUniversal, getEntityKey } from "./utils/extractors";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

function apiUrl(path) {
  return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

// --- Central fetch helper ---
async function apiFetch(path, options = {}) {
  const res = await fetch(apiUrl(path), options);
  let data;
  const isJson = res.headers.get("content-type")?.includes("application/json");
  try {
    data = isJson ? await res.json() : await res.text();
  } catch {
    data = res.statusText || "Unknown error";
  }
  if (!res.ok) {
    const msg = typeof data === "object" ? (data.error || data.message || res.statusText) : data;
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return data;
}

// ========== AUTH ==========
export async function login(username, password) {
  return apiFetch("/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
}

// ========== ORDERS ==========
export async function fetchOrders(token) {
  return apiFetch("/api/orders", {
    headers: { Authorization: `Bearer ${token}` }
  });
}
export async function syncOrders(token, orders) {
  return apiFetch("/api/orders/sync", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ orders }),
  });
}

export async function fetchCustomerStatement(token, customerId) {
  return apiFetch("/api/customers/statement", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ customerId }),
  });
}

// ========== PURCHASE ORDERS ==========
export async function syncPurchOrders(token, orders) {
  return apiFetch("/api/purchaseorders/sync", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ orders }),
  });
}

// ========== WAREHOUSE ==========
export async function fetchWarehouses(token) {
  return fetchAllEntityPages(token, "Warehouses", 2000);
}
export async function syncWarehouseEntries(token, entries) {
  return apiFetch("/api/warehouse/sync", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ entries }),
  });
}

// ========== ITEMS & RELATED DATA ==========
export async function fetchItems(token) {
  return fetchAllEntityPages(token, "ItemsGet", 2000);
}
export async function fetchTransport(token) {
  return fetchAllEntityPages(token, "TransportM", 2000);
}
export async function fetchContacts(token) {
  return fetchAllEntityPages(token, "Contacts", 2000);
}
export async function fetchCustomerBalances(token) {
  return fetchAllEntityPages(token, "CustBalance", 2000);
}
export async function fetchBarcodes(token) {
  return fetchAllEntityPages(token, "Barcodes", 2000);
}
export async function fetchSerialNumbers(token) {
  return fetchAllEntityPages(token, "SerialNumbers", 2000);
}
export async function fetchItemSeries(token) {
  return fetchAllEntityPages(token, "Series", 2000);
}
export async function fetchPayMethods(token) {
  return fetchAllEntityPages(token, "PaymMethod", 2000);
}
export async function fetchBanks(token) {
  return fetchAllEntityPages(token, "BankCNT", 2000);
}
export async function fetchCompBanks(token) {
  return fetchAllEntityPages(token, "BanksCMP", 2000);
}
export async function fetchCards(token) {
  return fetchAllEntityPages(token, "Cards", 2000);
}
export async function fetchAddresses(token) {
  return fetchAllEntityPages(token, "AddressSc", 2000);
}
export async function fetchSupplierBranches(token) {
  return fetchAllEntityPages(token, "SupplBrSc", 2000);
}
export async function fetchCompanies(token) {
  return fetchAllEntityPages(token, "Company", 2000);
}
export async function fetchDoys(token) {
  return fetchAllEntityPages(token, "Doys", 2000);
}
export async function fetchVatStatuses(token) {
  return fetchAllEntityPages(token, "VatStatus", 2000);
}
export async function fetchVatCategories(token) {
  return fetchAllEntityPages(token, "VatCategory", 2000);
}

// ========== AADE DATA & CUSTOMERS ==========

export async function addCustomer(token, customerData) {
  return apiFetch("/api/customers", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(customerData),
  });
}
export async function fetchAadeData(token, afm) {
  return apiFetch("/api/data/AADEData", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ AFM: afm }),
  });
}

// ========== SYNC & STATUS ==========
export async function fetchSyncStatus(token) {
  return apiFetch("/api/sync-status", {
    headers: { Authorization: `Bearer ${token}` }
  });
}
export async function runSyncAll(token) {
  return apiFetch("/api/sync-all", {
    headers: { Authorization: `Bearer ${token}` }
  });
}

// ========== CENSUS / APOGRAFI ==========
export async function fetchCensusList(token) {
  return fetchAllEntityPages(token, "ApografiSC", 2000);
}
export async function syncApografiEntries(token, entries) {
  return apiFetch("/api/apografi/sync", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ entries }),
  });
}

// ========== GENERIC ENTITY FETCH ==========
export async function fetchEntityData(token, entity) {
  return fetchAllEntityPages(token, entity, 2000);
}

// ========== ADMIN: CLIENTS ==========
export async function fetchClients(token) {
  return apiFetch("/api/clients", {
    headers: { Authorization: `Bearer ${token}` }
  });
}
export async function addClient(token, data) {
  return apiFetch("/api/clients", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(data)
  });
}
export async function updateClient(token, id, data) {
  return apiFetch(`/api/clients/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(data)
  });
}
export async function deleteClient(token, id) {
  return apiFetch(`/api/clients/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` }
  });
}

// --- NEW: MODULES RELATION ENDPOINTS for CLIENTS ---
export async function fetchClientModules(token, clientId) {
  return apiFetch(`/api/clients/${clientId}/modules`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}
export async function setClientModules(token, clientId, modules) {
  return apiFetch(`/api/clients/${clientId}/modules`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ modules }),
  });
}

// ========== ADMIN: USERS ==========
export async function fetchUsers(token) {
  return apiFetch("/api/users", {
    headers: { Authorization: `Bearer ${token}` }
  });
}
export async function addUser(token, data) {
  return apiFetch("/api/users", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(data)
  });
}
export async function updateUser(token, id, data) {
  return apiFetch(`/api/users/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(data)
  });
}
export async function deleteUser(token, id) {
  return apiFetch(`/api/users/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` }
  });
}

// --- NEW: MODULES RELATION ENDPOINTS for USERS ---
export async function fetchUserModules(token, userId) {
  return apiFetch(`/api/users/${userId}/modules`, {
    headers: { Authorization: `Bearer ${token}` }
  });
}
export async function setUserModules(token, userId, modules) {
  return apiFetch(`/api/users/${userId}/modules`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ modules }),
  });
}

// ========== ADMIN: MODULES ==========
export async function fetchModules(token) {
  return apiFetch("/api/modules", {
    headers: { Authorization: `Bearer ${token}` }
  });
}

// ========== USAGE / COMMERCIAL QUOTA ==========
export async function getMyUsage(token) {
  return apiFetch("/api/usage/me", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getAadeDailyAdminUsage(token, clientId = null) {
  const qs = new URLSearchParams();

  if (clientId) {
    qs.append("clientId", clientId);
  }

  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return apiFetch(`/api/usage/admin/aade-daily${suffix}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function saveClientUsagePlan(token, payload) {
  return apiFetch("/api/usage/admin/client-plan", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function getClientUsagePlan(token, clientId) {
  return apiFetch(`/api/usage/admin/client-plan/${clientId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

// ========== ITEM MANAGEMENT ==========
export async function updateItemToPylon(token, item) {
  return apiFetch("/api/items", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(item)
  });
}

//---------------- RECEIPTS  ----------------
export async function fetchReceipts(token) {
  return apiFetch("/api/receipts", {
    headers: { Authorization: `Bearer ${token}` }
  });
}
export async function createReceipt(token, form) {
  return apiFetch("/api/receipts", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(form)
  });
}
export async function updateReceipt(token, id, form) {
  return apiFetch(`/api/receipts/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(form)
  });
}
export async function deleteReceipt(token, id) {
  return apiFetch(`/api/receipts/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` }
  });
}
export async function syncReceipts(token, receipts) {
  return apiFetch("/api/receipts/sync", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ receipts })
  });
}
export async function fetchBranches(token) {
  return fetchAllEntityPages(token, "AddressSc", 2000);
}
export async function fetchLotnumbers(token) {
  return fetchAllEntityPages(token, "LotnumbersG", 2000);
}
export async function fetchItemsXrwma(token) {
  return fetchAllEntityPages(token, "ItemsXrwma", 2000);
}

/**
 * Fetches all paged data from the backend for the given entity.
 * Returns a flat array of all rows for that entity.
 */
export async function fetchAllEntityPages(token, entity, pageSize = 2000) {
  let allRows = [];
  let page = 1;
  const entityKey = getEntityKey(entity);

  while (true) {
    const url = `/api/data/${entity}?packagenumber=${page}&packagesize=${pageSize}`;
    const raw = await apiFetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    // The only line you need for robust parsing:
    const rows = extractUniversal(raw, entityKey);

    allRows = allRows.concat(rows);
    if (rows.length < pageSize) break; // last page
    page++;
  }
  return allRows;
}
/// Test Pylon Settings
export async function testPylonLogin(form, token) {
  return apiFetch("/api/clients/test-pylon-login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(form)
  });
}
/// EditOrders
export async function fetchEditOrders(token) {
  return fetchAllEntityPages(token, "OrdersSalesB", 2000);
}
export async function editOrderToPylon(token, order_id, lines) {
  return apiFetch("/api/orders/edit", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ order_id, lines })
  });
}
// ========== AADE / myDATA IMPORTS ==========
export async function fetchAadeDocs(token, { mark, exactMark, dateFrom, dateTo }) {
  const params = new URLSearchParams();

  if (mark) params.append("mark", mark);
  if (exactMark) params.append("exactMark", exactMark);
  if (dateFrom) params.append("dateFrom", dateFrom);
  if (dateTo) params.append("dateTo", dateTo);

  return apiFetch(`/api/aade/request-docs?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
export async function startAadeFetchJob(
  token,
  { mark, exactMark, dateFrom, dateTo, entityVatNumber }
) {
  return apiFetch("/api/aade/request-docs-job", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      mark,
      exactMark,
      dateFrom,
      dateTo,
      entityVatNumber,
    }),
  });
}

export async function enrichAadeInvoiceFromProvider(token, invoice) {
  return apiFetch("/api/aade/provider-invoice/enrich", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ invoice }),
  });
}

export async function getAadeFetchJob(token, jobId) {
  return apiFetch(`/api/aade/request-docs-job/${encodeURIComponent(jobId)}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getAadeFetchJobResults(token, jobId) {
  return apiFetch(
    `/api/aade/request-docs-job/${encodeURIComponent(jobId)}/results`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
}
// ========== AADE ITEM MAPPING ==========
export async function fetchAadeItemMapping(token, params) {
  const qs = new URLSearchParams();

  if (params.supplierVat) qs.append("supplierVat", params.supplierVat);
  if (params.aadeItemCode) qs.append("aadeItemCode", params.aadeItemCode);
  if (params.aadeItemDescr) qs.append("aadeItemDescr", params.aadeItemDescr);

  return apiFetch(`/api/aade/item-mapping?${qs.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function saveAadeItemMapping(token, payload) {
  return apiFetch("/api/aade/item-mapping", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function fetchAadeItemMappings(token) {
  return apiFetch("/api/aade/item-mappings", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
export async function importAadeInvoicesToPylon(token, payload) {
  return apiFetch("/api/aade/import-to-pylon", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function createAadeItemFromLine(token, payload) {
  return apiFetch("/api/aade/create-item-from-line", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function fetchMeasurementUnits(token) {
  return apiFetch("/api/measurement-units", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createAadeSupplier(token, payload) {
  return apiFetch("/api/aade/create-supplier", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}
export async function syncAadeAdditionCharges(token) {
  return apiFetch("/api/aade/addition-charges/sync", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
export async function syncAadeDocSeries(token) {
  return apiFetch("/api/aade/doc-series/sync", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
export async function syncAadePayMethods(token) {
  const rows = await fetchPayMethods(token);

  const payMethods = (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      PylonPayMethodID:
        row.Pylon_PaymentMethod_ID ||
        row.Pylon_ID ||
        row.PaymentMethod_ID ||
        row.HEID ||
        row.heid ||
        row.ID ||
        row.id ||
        "",
      Code:
        row.Code ||
        row.code ||
        row.HECODE ||
        row.hecode ||
        "",
      Name:
        row.Name ||
        row.name ||
        row.HENAME ||
        row.hename ||
        "",
      raw: row,
    }))
    .filter((row) => row.PylonPayMethodID || row.Code || row.Name);

  return {
    ok: true,
    count: payMethods.length,
    payMethods,
  };
}
export async function startAadeImportToPylon(token, payload) {
  return apiFetch("/api/aade/import-to-pylon/jobs", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function getAadeImportJob(token, jobId) {
  return apiFetch(`/api/aade/import-to-pylon/jobs/${jobId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getAadeImportJobResults(token, jobId) {
  return apiFetch(`/api/aade/import-to-pylon/jobs/${jobId}/results`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
export async function syncAadeServiceItems(token) {
  return apiFetch("/api/aade/service-items/sync", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function syncAadeSpecificSeries(token) {
  return apiFetch("/api/aade/aade-series/sync", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
export async function syncAadeLotsTracking(token) {
  return apiFetch("/api/aade/lots/sync", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
